import { useLocalization, createLazyResource, createMutation } from '@magicdoorinc/ui';
import { createMemo, createSignal } from 'solid-js';
import { useBankAccountsList } from '~/contexts/bank-accounts';
import { createMagicDoorContext } from '~/contexts/utils';
import { ChartOfAccountsRepository } from '~/repositories/chartOfAccountsRepository';
import { chartOfAccountTypeMap } from './map';
import type { ChartOfAccountDto, ChartOfAccountType, CreateChartOfAccountDto, UpdateChartOfAccountDto } from '~/swagger/portal/Api';

const repo = new ChartOfAccountsRepository();

type ChartOfAccountTreeItem = ChartOfAccountDto & { children: ChartOfAccountTreeItem[] };

export type ChartOfAccountGroup = {
  type: `${ChartOfAccountType}`;
  title: string;
  items: ChartOfAccountTreeItem[];
};

export type DefaultChartOfAccountType = 'rentIncome' | 'rentDeposit';

/**
 * The company's conventional account for a purpose, matched by name. Exported as a pure function
 * over a caller-supplied list so the same rule can be applied outside this context — the Genie
 * bridge resolves an account against its own fetch, because a lazily loaded resource that has not
 * been primed yet would make the same suggestion behave differently depending on timing.
 */
export const findDefaultChartOfAccount = (
  accounts: ChartOfAccountDto[] | undefined,
  type: DefaultChartOfAccountType
): ChartOfAccountDto | undefined =>
  accounts?.find((account) => {
    const name = account.name?.toLowerCase();
    if (!name) return false;
    if (type === 'rentIncome') {
      return name.startsWith('rent income') || name.startsWith('rentincome');
    }
    return name.startsWith('rent deposit') || name.startsWith('rentdeposit');
  });

export const [ChartOfAccountsProvider, useChartOfAccounts] = createMagicDoorContext('ChartOfAccounts', () => {
  const { t } = useLocalization();
  const [chartOfAccounts, { fetch, refetch, mutate }] = createLazyResource(() => repo.getChartOfAccounts());
  const { updateBankAccountChartOfAccount } = useBankAccountsList();
  const [currentId, setCurrentId] = createSignal<string>();

  const current = createMemo(() => (currentId() ? chartOfAccounts()?.find((p) => p.id === currentId()) : undefined));

  const chartOfAccountTree = createMemo(() => {
    const list = chartOfAccounts();
    if (list == null || list.length === 0) return [];
    const map = new Map<string, ChartOfAccountTreeItem>();
    list.forEach((item) => map.set(item.id, { ...item, children: [] }));
    return list.reduce((prev, curr) => {
      const node = map.get(curr.id) as ChartOfAccountTreeItem;
      if (node.parentId == null) {
        prev.push(node);
      } else {
        const parent = map.get(node.parentId);
        parent?.children.push(node);
      }
      return prev;
    }, [] as ChartOfAccountTreeItem[]);
  });

  const chartOfAccountGroups = createMemo(() => {
    return chartOfAccountTree()?.reduce((prev, curr) => {
      const group = prev.find((group) => group.type === curr.type);
      if (group) {
        group.items.push(curr);
      } else {
        prev.push({ type: curr.type, title: t(chartOfAccountTypeMap[curr.type]), items: [curr] });
      }
      return prev;
    }, [] as ChartOfAccountGroup[]);
  });

  const addChartOfAccount = createMutation(async (model: CreateChartOfAccountDto) => {
    const created = await repo.createChartOfAccount(model);
    mutate((prev) => [...(prev ?? []), created]);
    return created;
  });

  const editChartOfAccount = createMutation(async (id: string, model: UpdateChartOfAccountDto) => {
    const updated = await repo.updateChartOfAccount(id, model);
    mutate((prev) => prev?.map((t) => (t.id === updated.id ? updated : t)));
    return updated;
  });

  const deleteChartOfAccount = createMutation(async (accountId: string, replacementId: string) => {
    await repo.deleteChartOfAccount(accountId, replacementId);
    mutate((prev) => prev?.filter((t) => t.id !== accountId));
    updateBankAccountChartOfAccount({
      deletedChartOfAccountId: accountId,
      replacementChartOfAccountId: replacementId,
    });
  });

  const getChartOfAccountById = (id: string) => {
    return chartOfAccounts()?.find((p) => p.id === id);
  };

  const getDefaultChartOfAccountId = (type: DefaultChartOfAccountType) => findDefaultChartOfAccount(chartOfAccounts(), type)?.id;

  return {
    get chartOfAccounts() {
      fetch();
      return chartOfAccounts;
    },
    get chartOfAccountTree() {
      fetch();
      return chartOfAccountTree;
    },
    get chartOfAccountGroups() {
      fetch();
      return chartOfAccountGroups;
    },
    refetch,
    current,
    addChartOfAccount,
    editChartOfAccount,
    deleteChartOfAccount,
    setCurrentId,
    getChartOfAccountById,
    getDefaultChartOfAccountId,
  };
});
